/**
 * /night-vision — the arithmetic of seeing in the dark.
 *
 * One algorithm, two executors. gl.js runs it on the GPU, a pass per stage,
 * and repeats every formula of this file in GLSL; CpuPipeline below runs the
 * same stages on Float32 arrays. The CPU one is the reference: the tests feed
 * it real low-light photographs (the LOL pairs) and simulated night footage,
 * the browser stand renders the same frames through WebGL and compares, and it
 * draws the picture itself where WebGL2 is missing.
 *
 * What a camera can and cannot give in the dark. A phone or laptop camera
 * has an infrared-cut filter: it does not see heat and it does not see in
 * total darkness. What it does see at night is a little light (a street lamp,
 * the moon, a screen in the next room) buried in sensor noise. Everything here
 * is about pulling that light out of the noise honestly:
 *
 *   1. temporal — the same pixel over many frames: noise falls as √N while
 *      the scene stays. A recursive average with a per-pixel history length,
 *      clipped to the current frame's 3×3 neighbourhood (variance clipping,
 *      the TAA technique): a moving thing falls outside the box and restarts
 *      its history instead of leaving a ghost. The box width is the
 *      neighbourhood's own spread, so the rule scales with the noise by itself.
 *   2. spatial — an edge-aware 5×5 filter whose range is the noise the pixel
 *      still carries (the noise model σ²(L) = a + b·L, measured live from
 *      frame-to-frame differences, divided by its history), so a sharp edge
 *      stays and the grain goes; the colour is taken from a softer copy
 *      where it is mostly noise.
 *   3. exposure — a global gain from the log-average brightness that rises
 *      slowly and falls fast (a lamp switched on must not blind), a local gain
 *      that lifts the dark corners more than the lit middle, and a tone curve
 *      that compresses instead of clipping.
 *   4. look — natural colour, or one of the palettes of night-vision devices.
 *
 * Measured choices and the numbers behind them: docs/why/night-vision.md.
 */

/* ───────────────────────── colour ───────────────────────── */

export function srgbToLinear(v) {
    return v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
}
export function linearToSrgb(v) {
    if (!(v > 0)) return 0;
    if (v >= 1) return 1;
    return v <= 0.0031308 ? v * 12.92 : 1.055 * Math.pow(v, 1 / 2.4) - 0.055;
}
/** sRGB byte → linear, the table the CPU lane reads every pixel through. */
export const S2L = (() => {
    const t = new Float32Array(256);
    for (let i = 0; i < 256; i++) t[i] = srgbToLinear(i / 255);
    return t;
})();
export const LUMA = [0.2126, 0.7152, 0.0722];
export function luma(r, g, b) { return LUMA[0] * r + LUMA[1] * g + LUMA[2] * b; }

/* ───────────────────────── settings ───────────────────────── */

/**
 * Longest history a pixel may average, per «Накопление» level. A level is a
 * promise about the camera, not about the picture: «move» is for walking with
 * the phone, «tripod» for a phone that does not move at all.
 */
export const STACK = { off: 1, move: 4, normal: 12, still: 32, tripod: 96 };
/** Edge-aware filter strength: range = h · residual noise. */
export const DENOISE = { off: 0, low: 1.1, normal: 1.7, high: 2.6 };

export const PARAMS = {
    clipGamma: 1.25,      // half-width of the neighbourhood box, in its own σ
    historyKappa: 1.0,    // how fast a clipped pixel forgets: n ← n·e^(−κd)
    spatialSigma: 1.5,    // px, 5×5 kernel
    target: 0.18,         // log-average brightness the gain aims for (linear)
    white: 6.0,           // tone curve white point; the gain also stops where p99·gain passes 0.85·white
    localMin: 1 / 1.6,    // local gain clamp
    localMax: 4.0,
    gainMax: 128,
    noiseFloor: 1e-7,     // σ² floor so a perfectly clean source still filters a hair
};

/* ───────────────────────── tone ───────────────────────── */

/**
 * Extended Reinhard: x = W maps to 1, the shoulder compresses instead of
 * clipping. Measured against a soft knee (linear to 0.6, then a shoulder) on
 * the LOL pairs: 25.85 dB against 24.91 after matching exposure — the camera
 * that took the reference photos compresses the same way.
 */
export function tone(x, W) {
    if (!(x > 0)) return 0;
    const y = x * (1 + x / (W * W)) / (1 + x);
    return y > 1 ? 1 : y;
}

/**
 * How hard to lift the dark neighbourhoods, from the «Тени» slider (0–100) and
 * the scene's dynamic range (99th percentile over log-average). The lift is
 * for a lamp-and-shadows scene; on an evenly dim one it only moves the picture
 * away from what the camera saw (LOL pairs: −1…−2 dB). The gate opens across
 * the range real dim scenes do not reach: LOL median 4.2, 90th percentile 10.7.
 */
export function localStrength(shadowsPct, st) {
    const dr = st && st.logAvg > 0 ? st.p99 / st.logAvg : 0;
    return (Math.max(0, Math.min(100, shadowsPct)) / 100) * 0.6 * smoothstep(6, 20, dr);
}

/** Local gain: dark neighbourhoods are lifted more than lit ones. */
export function localGain(xLocal, target, s, lo, hi) {
    if (!(s > 0)) return 1;
    const g = Math.pow(Math.max(xLocal, 1e-5) / target, -s);
    return g < lo ? lo : g > hi ? hi : g;
}

export function smoothstep(e0, e1, x) {
    const t = Math.min(1, Math.max(0, (x - e0) / (e1 - e0)));
    return t * t * (3 - 2 * t);
}

/* ───────────────────────── palettes ───────────────────────── */

/**
 * The looks of night-vision devices, as colour stops over display brightness.
 * green — the P43 phosphor of Gen 2/3 intensifier tubes; white — P45, the
 * «white phosphor» of current tubes; red — keeps the eyes' dark adaptation
 * (astronomers' and hunters' light); iron and rainbow — the palettes of thermal
 * imagers. ⚠ A camera does not see heat: these two colour BRIGHTNESS, and the
 * page says so next to them.
 */
export const PALETTES = {
    green: [[0, 0.004, 0.012, 0.004], [0.18, 0.03, 0.22, 0.04], [0.5, 0.2, 0.68, 0.16], [0.82, 0.6, 0.98, 0.45], [1, 0.9, 1, 0.82]],
    white: [[0, 0.008, 0.01, 0.016], [0.3, 0.2, 0.23, 0.28], [0.7, 0.68, 0.73, 0.8], [1, 0.97, 0.98, 1]],
    red: [[0, 0, 0, 0], [0.45, 0.42, 0.0, 0.0], [0.85, 0.92, 0.08, 0.04], [1, 1, 0.32, 0.22]],
    mono: [[0, 0, 0, 0], [1, 1, 1, 1]],
    iron: [[0, 0, 0, 0.02], [0.14, 0.12, 0.0, 0.42], [0.3, 0.45, 0.0, 0.6], [0.48, 0.78, 0.12, 0.42], [0.66, 0.95, 0.42, 0.06], [0.84, 1, 0.75, 0.1], [1, 1, 1, 0.92]],
    rainbow: [[0, 0.19, 0.07, 0.23], [0.13, 0.27, 0.36, 0.91], [0.27, 0.14, 0.67, 0.97], [0.4, 0.1, 0.9, 0.74], [0.53, 0.44, 0.99, 0.37], [0.67, 0.83, 0.88, 0.2], [0.8, 0.99, 0.6, 0.12], [0.92, 0.9, 0.27, 0.05], [1, 0.6, 0.06, 0.02]],
    blackhot: [[0, 1, 1, 1], [1, 0, 0, 0]],
};
export const LOOKS = ['color', 'green', 'white', 'mono', 'red', 'iron', 'rainbow', 'blackhot'];

/** 256 RGBA bytes for one palette (or null for natural colour). */
export function paletteLUT(name) {
    const stops = PALETTES[name];
    if (!stops) return null;
    const out = new Uint8Array(256 * 4);
    for (let i = 0; i < 256; i++) {
        const x = i / 255;
        let k = 0;
        while (k < stops.length - 2 && x > stops[k + 1][0]) k++;
        const a = stops[k], b = stops[k + 1];
        const t = b[0] > a[0] ? Math.min(1, Math.max(0, (x - a[0]) / (b[0] - a[0]))) : 0;
        for (let c = 0; c < 3; c++) out[i * 4 + c] = Math.round(255 * (a[c + 1] + (b[c + 1] - a[c + 1]) * t));
        out[i * 4 + 3] = 255;
    }
    return out;
}

/* ───────────────────────── noise model ───────────────────────── */

/**
 * σ²(L) = a + b·L from two consecutive frames sampled at the SAME pixels
 * (no averaging — averaging would hide the noise it is measuring). In a still
 * part of the scene the difference of two frames is pure noise with variance
 * 2σ². Robust per brightness bin (MAD), so a person walking through a third of
 * the frame does not read as noise. Returns null until there is enough.
 */
export function fitNoise(prev, cur, opts) {
    const o = opts || {};
    const NB = 10, bins = [];
    for (let i = 0; i < NB; i++) bins.push([]);
    const lo = Math.log(2e-4), hi = Math.log(0.6);
    const n = Math.min(prev.length, cur.length);
    for (let i = 0; i < n; i++) {
        const p = prev[i], c = cur[i];
        const s = 0.5 * (p + c);
        if (!(s > 0) || s > 0.6) continue;
        const k = Math.max(0, Math.min(NB - 1, Math.floor((Math.log(Math.max(s, 2e-4)) - lo) / (hi - lo) * NB)));
        bins[k].push(c - p, s);
    }
    const pts = [];
    for (const b of bins) {
        const m = b.length >> 1;
        if (m < (o.minPerBin || 40)) continue;
        const d = new Float32Array(m), s = new Float32Array(m);
        for (let i = 0; i < m; i++) { d[i] = b[2 * i]; s[i] = b[2 * i + 1]; }
        const med = median(d);
        for (let i = 0; i < m; i++) d[i] = Math.abs(d[i] - med);
        const mad = median(d);
        const sigma = 1.4826 * mad / Math.SQRT2;
        pts.push({ s: mean(s), v: sigma * sigma, w: m });
    }
    if (pts.length < 2) {
        if (pts.length === 1) return { a: pts[0].v, b: 0, n: pts[0].w };
        return null;
    }
    // weighted least squares v = a + b·s, then a, b ≥ 0
    let sw = 0, ss = 0, sv = 0, sss = 0, ssv = 0;
    for (const p of pts) { const w = p.w; sw += w; ss += w * p.s; sv += w * p.v; sss += w * p.s * p.s; ssv += w * p.s * p.v; }
    const det = sw * sss - ss * ss;
    let a, b;
    if (Math.abs(det) < 1e-18) { a = sv / sw; b = 0; }
    else { b = (sw * ssv - ss * sv) / det; a = (sv - b * ss) / sw; }
    if (b < 0) { b = 0; a = sv / sw; }
    if (a < 0) { a = 0; b = ssv / Math.max(sss, 1e-12); }
    return { a, b, n: sw };
}

/**
 * The noise model of ONE picture (a photo opened from a file has no second
 * frame): neighbouring pixels stand in for two looks at the same point. Edges
 * inflate a difference, so only pairs whose neighbourhood is flat enough take
 * part, and the per-bin MAD of fitNoise ignores the rest.
 * @param {Uint8Array|Uint8ClampedArray} rgba @param {number} w @param {number} h
 */
export function fitNoiseSingle(rgba, w, h) {
    const step = Math.max(1, Math.floor(Math.sqrt((w * h) / 120000)));
    const a = [], b = [];
    const L = (i) => luma(S2L[rgba[i]], S2L[rgba[i + 1]], S2L[rgba[i + 2]]);
    for (let y = 1; y < h - 1; y += step) {
        for (let x = 1; x < w - 2; x += step) {
            const i = (y * w + x) * 4;
            const l0 = L(i), l1 = L(i + 4), up = L(i - w * 4), dn = L(i + w * 4);
            // vertical gradient as the edge test: a horizontal pair across an edge would read as noise
            if (Math.abs(up - dn) > 0.5 * (l0 + 0.002)) continue;
            a.push(l0); b.push(l1);
        }
    }
    return fitNoise(Float32Array.from(a), Float32Array.from(b), { minPerBin: 30 });
}

function median(arr) {
    const a = Float32Array.from(arr).sort();
    const m = a.length >> 1;
    return a.length % 2 ? a[m] : 0.5 * (a[m - 1] + a[m]);
}
function mean(arr) { let s = 0; for (let i = 0; i < arr.length; i++) s += arr[i]; return arr.length ? s / arr.length : 0; }

/* ───────────────────────── exposure ───────────────────────── */

/**
 * The global gain. Aims the log-average brightness of the denoised picture at
 * `target`; rises with time constant tauUp and falls with tauDown — opening a
 * door onto a lit corridor must not white out the screen for a second.
 * Highlights: the gain stops where the brightest percent would be pushed past
 * the white point, unless the rest of the frame is so dark it needs it.
 */
export class GainController {
    constructor(o) {
        o = o || {};
        this.min = o.min || 1;
        this.max = o.max || PARAMS.gainMax;
        this.tauUp = o.tauUp || 0.6;
        this.tauDown = o.tauDown || 0.12;
        this.gain = o.start || 1;
        this.primed = false;
    }
    /** @param {{logAvg:number, p99:number}} st linear brightness of the frame before gain */
    want(st, target, white) {
        const T = target || PARAMS.target, W = white || PARAMS.white;
        let g = T / Math.max(st.logAvg, 1e-6);
        if (st.p99 > 0) {
            const hl = (0.85 * W) / st.p99;
            // the highlight limit gives way when obeying it leaves the frame at
            // under a third of the target — a lamp in the corner of a dark yard
            if (g > hl) g = Math.max(hl, g / 3);
        }
        return Math.min(this.max, Math.max(this.min, g));
    }
    update(st, dt, target, white) {
        const want = this.want(st, target, white);
        if (!this.primed) { this.gain = want; this.primed = true; return this.gain; }
        const up = want > this.gain;
        const tau = up ? this.tauUp : this.tauDown;
        const k = 1 - Math.exp(-Math.max(0, dt) / tau);
        this.gain = Math.exp(Math.log(this.gain) + (Math.log(want) - Math.log(this.gain)) * k);
        return this.gain;
    }
}

/** Log-average and 99th percentile of linear brightness samples. */
export function brightnessStats(samples) {
    const n = samples.length;
    if (!n) return { logAvg: 0, p99: 0, mean: 0 };
    let sl = 0, sm = 0;
    for (let i = 0; i < n; i++) { const v = samples[i]; sl += Math.log(v + 1e-4); sm += v; }
    const sorted = Float32Array.from(samples).sort();
    return { logAvg: Math.max(0, Math.exp(sl / n) - 1e-4), p99: sorted[Math.min(n - 1, Math.floor(n * 0.99))], mean: sm / n };
}

/**
 * Reads the small grid both executors hand back after a frame and decides the
 * next frame's gain and noise model, plus what the page tells the person.
 *   raw — RGBA bytes of the camera frame at single grid pixels (noise meter)
 *   avg — r = √(averaged linear luma)·255, g = motion·255, b = history/128·255,
 *         a = the scene's shift this frame (/16 px·255; 0 unsure, 255 no search)
 */
export class Controller {
    constructor() {
        this.gainCtl = new GainController();
        this.noise = null;
        this._prevRaw = null;
        this._prevRawFrame = -1;
        this.last = null;
    }
    reset() { this.gainCtl = new GainController(); this._prevRaw = null; this.last = null; }

    /**
     * @param {{raw:Uint8Array, avg:Uint8Array, w:number, h:number, frame:number}} st
     * @param {number} dt seconds since the previous update
     * @param {{ev?:number, manualGain?:number, stack?:number}} o
     */
    update(st, dt, o) {
        o = o || {};
        const n = st.w * st.h;
        const raw = new Float32Array(n), lum = new Float32Array(n);
        let rawCodeSum = 0, moving = 0, histSum = 0;
        for (let i = 0; i < n; i++) {
            const r = st.raw[i * 4], g = st.raw[i * 4 + 1], b = st.raw[i * 4 + 2];
            raw[i] = luma(S2L[r], S2L[g], S2L[b]);
            rawCodeSum += 0.2126 * r + 0.7152 * g + 0.0722 * b;
            const v = st.avg[i * 4] / 255;
            lum[i] = v * v;
            if (st.avg[i * 4 + 1] > 127) moving++;
            histSum += st.avg[i * 4 + 2] * 128 / 255;
        }
        // the scene's shift this frame, px of the processing frame (the same in every cell); null — no search ran
        const shakePx = st.avg[3] === 255 ? null : st.avg[3] / 255 * 16;
        if (this._prevRaw && st.frame !== this._prevRawFrame) {
            const fit = fitNoise(this._prevRaw, raw, { minPerBin: 30 });
            if (fit) {
                this.noise = this.noise
                    ? { a: 0.85 * this.noise.a + 0.15 * fit.a, b: 0.85 * this.noise.b + 0.15 * fit.b }
                    : fit;
            }
        }
        this._prevRaw = raw; this._prevRawFrame = st.frame;
        const bs = brightnessStats(lum);
        const target = PARAMS.target * Math.pow(2, o.ev || 0);
        let gain = this.gainCtl.update(bs, dt, target, PARAMS.white);
        if (o.manualGain > 0) gain = o.manualGain;
        const motionFrac = moving / n;
        const rawMeanCode = rawCodeSum / n;
        const history = Math.max(1, histSum / n);
        const snr = effectiveSnr(bs.logAvg, this.noise, history);
        // «the camera gets essentially no light»: the frame is a few codes above
        // black AND what is left after averaging is mostly noise
        const dark = rawMeanCode < 4 && snr < 3;
        this.last = { gain, noise: this.noise, logAvg: bs.logAvg, p99: bs.p99, motionFrac, rawMeanCode, snr, dark, history, shakePx };
        return this.last;
    }
}

/**
 * «Авто» накопление: how long a pixel may average follows how much the camera
 * moves, the way a phone's night mode reads its gyroscope — here from the
 * shake search itself. The shift per frame (normalised to a 1280-px-wide
 * frame, smoothed over ~0.6 s) picks the level; where no search runs (the
 * integer lane) or it is not sure (a dark, flat scene), the share of pixels
 * the motion test threw out speaks instead — misaligned frames land there.
 * Changes are slow toward more frames (the picture gets quieter, nothing is
 * lost by waiting) and quick toward fewer (a smear is visible at once), with a
 * band around each boundary so a hand near a threshold does not flicker it.
 */
export class AutoStack {
    constructor() { this.reset(); }
    reset() { this.level = 'normal'; this.shake = null; this.downT = 0; this.upT = 0; this.changes = 0; }

    /**
     * @param {number|null} shake  px per frame at a 1280-px-wide frame; null — unknown
     * @param {number} motionFrac  share of grid cells the motion test marked
     * @param {number} dt          seconds since the last call
     * @returns {string} a key of STACK
     */
    update(shake, motionFrac, dt) {
        dt = Math.max(0, Math.min(0.5, dt || 0));
        if (shake != null && isFinite(shake)) {
            // a jolt is taken at once, calm is believed slowly
            const tau = this.shake == null || shake > this.shake ? 0.12 : 0.6;
            this.shake = this.shake == null ? shake : this.shake + (shake - this.shake) * Math.min(1, dt / tau);
        }
        const known = shake != null && this.shake != null;
        const cur = AUTO_LADDER.indexOf(this.level);
        let want = known ? AutoStack.rung(this.shake, AUTO_SHAKE, cur) : 3;
        want = Math.min(want, AutoStack.rung(motionFrac, known ? AUTO_MOTION_KNOWN : AUTO_MOTION_BLIND, cur));
        if (want > cur + 1) want = cur + 1;   // up one rung at a time: the picture gets quieter in steps you can see
        if (want < cur) {
            // toward fewer frames: a smear shows at once
            this.upT = 0; this.downT += dt;
            if (this.downT >= 0.2) { this.level = AUTO_LADDER[want]; this.downT = 0; this.changes++; }
        } else if (want > cur) {
            // toward more: after a calm second and a half (three before the tripod)
            this.downT = 0; this.upT += dt;
            if (this.upT >= (want === 3 ? 3 : 1.5)) { this.level = AUTO_LADDER[want]; this.upT = 0; this.changes++; }
        } else { this.downT = 0; this.upT = 0; }
        return this.level;
    }

    /**
     * Index on AUTO_LADDER for a measure where bigger means «more motion»:
     * boundaries[i] separates rung i (fewer frames) from rung i+1; leaving the
     * current rung needs the value past the boundary by a quarter.
     */
    static rung(v, boundaries, cur) {
        let r = 0;
        for (let i = 0; i < boundaries.length; i++) {
            const b = boundaries[i];
            const t = cur <= i ? b * 0.8 : b * 1.25;   // up the ladder needs calmer than b, down needs worse than b
            if (v < t) r = i + 1; else break;
        }
        return r;
    }
}
/** move (4) → normal (12) → still (32) → tripod (96) */
export const AUTO_LADDER = ['move', 'normal', 'still', 'tripod'];
/** px/frame at 1280 px: walking · hand-held · propped · fixed */
export const AUTO_SHAKE = [6, 1.2, 0.35];
/** share of cells the motion test threw out — when the shift is not known, and when it is (a scene that moves) */
export const AUTO_MOTION_BLIND = [0.35, 0.12, 0.04];
export const AUTO_MOTION_KNOWN = [0.5, 0.3, 0.15];

/**
 * How much the picture can still be trusted: signal over the noise left after
 * averaging, at the frame's typical brightness. Below ~2 the camera is getting
 * essentially no light and the page says so instead of amplifying noise.
 */
export function effectiveSnr(level, noise, history) {
    if (!(level > 0) || !noise) return Infinity;
    const sigma = Math.sqrt(Math.max(noise.a + noise.b * level, PARAMS.noiseFloor)) / Math.sqrt(Math.max(1, history || 1));
    return level / sigma;
}

/* ───────────────────────── the CPU reference ───────────────────────── */

/**
 * The whole pipeline on the CPU, stage for stage what gl.js draws.
 * Input and output are RGBA bytes of w×h. Used by the tests and as the
 * fallback renderer (at a reduced size) where WebGL2 is unavailable.
 */
export class CpuPipeline {
    constructor(w, h) {
        this.w = w; this.h = h;
        const n = w * h;
        this.cur = new Float32Array(n * 3);
        this.acc = new Float32Array(n * 3);
        this.hist = new Float32Array(n);
        this.motion = new Float32Array(n);
        this.out = new Uint8ClampedArray(n * 4);
        this.started = false;
        this.hw = Math.max(1, w >> 1); this.hh = Math.max(1, h >> 1);
        this.pyr = null;
    }

    reset() { this.started = false; }

    /**
     * @param {Uint8Array|Uint8ClampedArray} rgba input frame
     * @param {object} p {stack, denoise, gain, local, target, white, look, lut, shift:[dx,dy], noise:{a,b}, detail, motionTint}
     */
    process(rgba, p) {
        const { w, h } = this, n = w * h;
        const cur = this.cur, acc = this.acc, hist = this.hist;
        for (let i = 0, j = 0; i < n; i++, j += 3) {
            cur[j] = S2L[rgba[i * 4]]; cur[j + 1] = S2L[rgba[i * 4 + 1]]; cur[j + 2] = S2L[rgba[i * 4 + 2]];
        }
        const nMax = Math.max(1, p.stack || 1);
        this.lastShift = null;
        if (p.align && this.started && nMax > 1 && this.pyr) {
            // the scene's motion since the last frame, searched on the half-size pyramids (gl.js does the same)
            const hw = this.hw, hh = this.hh, cl = new Float32Array(hw * hh);
            for (let y = 0; y < hh; y++) for (let x = 0; x < hw; x++) {
                let v = 0;
                for (let oy = 0; oy < 2; oy++) for (let ox = 0; ox < 2; ox++) {
                    const k = (Math.min(h - 1, 2 * y + oy) * w + Math.min(w - 1, 2 * x + ox)) * 3;
                    v += luma(cur[k], cur[k + 1], cur[k + 2]);
                }
                cl[y * hw + x] = v / 4;
            }
            const prev = this.pyr.map((lv) => { const d = new Float32Array(lv.w * lv.h); for (let i = 0; i < d.length; i++) d[i] = lv.d[i * 4 + 3]; return { w: lv.w, h: lv.h, d }; });
            const lv = alignLevels(hw, hh);
            const sh = shiftSearch(lumaPyramid(cl, hw, hh), prev, lv.a, p.alignOpts || { fine: true });
            this.lastShift = sh;
            p = Object.assign({}, p, { shift: sh.q >= 0.02 ? [sh.dx, sh.dy] : [0, 0] });
        }
        if (!this.started || nMax <= 1) {
            acc.set(cur);
            hist.fill(1);
            this.motion.fill(0);
            this.started = true;
        } else {
            this._temporal(p, nMax);
        }
        this._buildPyramid();
        this._spatialAndTone(p);
        return this.out;
    }

    /**
     * The recursive average, exactly as the temporal pass of gl.js computes it:
     * the history is read where the scene was (shift), over the 4×4 texels
     * around that point — Catmull-Rom for the colour (kept inside the inner
     * 2×2: no overshoot at hard edges), bilinear for the history length, and a
     * 3×3 tent mean of luma for the motion test against the same tent mean of
     * the new frame.
     */
    _temporal(p, nMax) {
        const { w, h } = this;
        const cur = this.cur, acc = this.acc, hist = this.hist, mot = this.motion;
        const prevAcc = Float32Array.from(acc), prevHist = Float32Array.from(hist);
        const dx = (p.shift && p.shift[0]) || 0, dy = (p.shift && p.shift[1]) || 0;
        const G = PARAMS.clipGamma, K = PARAMS.historyKappa;
        const zLo = p.zLo || 2.5, zHi = p.zHi || 4.5;
        const noise = p.noise || { a: 0, b: 0 };
        const useZ = p.zTest !== false;
        const m = [0, 0, 0], s = [0, 0, 0], hc = [0, 0, 0], lo = [0, 0, 0], hi = [0, 0, 0];
        const cx = [0, 0, 0, 0], cy = [0, 0, 0, 0], tx = [0, 0, 0, 0], ty = [0, 0, 0, 0];
        const TW = [0.25, 0.5, 0.25];
        for (let y = 0; y < h; y++) {
            for (let x = 0; x < w; x++) {
                const i = y * w + x, j = i * 3;
                // the new frame: 3×3 box mean and σ per channel, tent mean of luma
                m[0] = m[1] = m[2] = 0; s[0] = s[1] = s[2] = 0;
                let lc = 0;
                for (let oy = -1; oy <= 1; oy++) {
                    const yy = y + oy < 0 ? 0 : y + oy >= h ? h - 1 : y + oy;
                    for (let ox = -1; ox <= 1; ox++) {
                        const xx = x + ox < 0 ? 0 : x + ox >= w ? w - 1 : x + ox;
                        const k = (yy * w + xx) * 3;
                        for (let c = 0; c < 3; c++) { const v = cur[k + c]; m[c] += v; s[c] += v * v; }
                        lc += luma(cur[k], cur[k + 1], cur[k + 2]) * TW[ox + 1] * TW[oy + 1];
                    }
                }
                const hx = x + 0.5 - dx, hy = y + 0.5 - dy;
                if (hx < 0 || hy < 0 || hx > w || hy > h) {
                    acc[j] = cur[j]; acc[j + 1] = cur[j + 1]; acc[j + 2] = cur[j + 2];
                    hist[i] = 1; mot[i] = 0;
                    continue;
                }
                const fx = hx - 0.5, fy = hy - 0.5, bx = Math.floor(fx), by = Math.floor(fy);
                const ttx = fx - bx, tty = fy - by;
                crWeights(ttx, cx); crWeights(tty, cy); tentBilinear(ttx, tx); tentBilinear(tty, ty);
                hc[0] = hc[1] = hc[2] = 0; lo[0] = lo[1] = lo[2] = Infinity; hi[0] = hi[1] = hi[2] = -Infinity;
                let lh = 0, n = 0;
                for (let ky = 0; ky < 4; ky++) {
                    const yy = Math.max(0, Math.min(h - 1, by + ky - 1));
                    for (let kx = 0; kx < 4; kx++) {
                        const xx = Math.max(0, Math.min(w - 1, bx + kx - 1));
                        const q = (yy * w + xx) * 3, wc = cx[kx] * cy[ky];
                        const r = prevAcc[q], g = prevAcc[q + 1], b = prevAcc[q + 2];
                        hc[0] += r * wc; hc[1] += g * wc; hc[2] += b * wc;
                        lh += luma(r, g, b) * tx[kx] * ty[ky];
                        if ((kx === 1 || kx === 2) && (ky === 1 || ky === 2)) {
                            if (r < lo[0]) lo[0] = r; if (r > hi[0]) hi[0] = r;
                            if (g < lo[1]) lo[1] = g; if (g > hi[1]) hi[1] = g;
                            if (b < lo[2]) lo[2] = b; if (b > hi[2]) hi[2] = b;
                            n += prevHist[yy * w + xx] * (kx === 1 ? 1 - ttx : ttx) * (ky === 1 ? 1 - tty : tty);
                        }
                    }
                }
                // motion test: tent means of the frame and of the history, in units of
                // the noise they can carry (Σw² = 0.140625 of σ², the history's shrunk
                // by its length). Texture sliding through a pixel moves the mean by
                // more than noise long before it leaves the neighbourhood's own spread
                // — the box clip alone smeared a moving object (docs/why/night-vision.md).
                let mz = 0;
                if (useZ) {
                    const vp = Math.max(noise.a + noise.b * Math.max(lc, 0), PARAMS.noiseFloor);
                    const z = Math.abs(lc - lh) / Math.sqrt(vp * 0.140625 * (1 + 1 / Math.max(n, 1)));
                    mz = smoothstep(zLo, zHi, z);
                }
                let d = 0;
                for (let c = 0; c < 3; c++) {
                    let v = hc[c] < lo[c] ? lo[c] : hc[c] > hi[c] ? hi[c] : hc[c];
                    const mu = m[c] / 9;
                    const sd = Math.sqrt(Math.max(0, s[c] / 9 - mu * mu)) + 1e-4;
                    const a = Math.min(Math.max(v, mu - G * sd), mu + G * sd);
                    const dc = Math.abs(v - a) / sd;
                    if (dc > d) d = dc;
                    hc[c] = a;
                }
                const nh = Math.min(n * Math.exp(-K * d) * (1 - mz) + 1, nMax);
                for (let c = 0; c < 3; c++) acc[j + c] = hc[c] + (cur[j + c] - hc[c]) / nh;
                hist[i] = nh;
                mot[i] = Math.max(mz, Math.min(1, d * 0.5));
            }
        }
    }

    /** Half-size image then box levels: the GPU's half-res texture and its mipmaps. */
    _buildPyramid() {
        const { w, h, acc } = this;
        const levels = [];
        let lw = this.hw, lh = this.hh;
        let lv = new Float32Array(lw * lh * 4);
        for (let y = 0; y < lh; y++) for (let x = 0; x < lw; x++) {
            const o = (y * lw + x) * 4;
            for (let c = 0; c < 3; c++) {
                let v = 0;
                for (let oy = 0; oy < 2; oy++) for (let ox = 0; ox < 2; ox++) {
                    const xx = Math.min(w - 1, 2 * x + ox), yy = Math.min(h - 1, 2 * y + oy);
                    v += acc[(yy * w + xx) * 3 + c];
                }
                lv[o + c] = v / 4;
            }
            lv[o + 3] = luma(lv[o], lv[o + 1], lv[o + 2]);
        }
        levels.push({ w: lw, h: lh, d: lv });
        while (lw > 1 || lh > 1) {
            const nw = Math.max(1, lw >> 1), nh = Math.max(1, lh >> 1);
            const nd = new Float32Array(nw * nh * 4);
            for (let y = 0; y < nh; y++) for (let x = 0; x < nw; x++) {
                for (let c = 0; c < 4; c++) {
                    let v = 0;
                    for (let oy = 0; oy < 2; oy++) for (let ox = 0; ox < 2; ox++) {
                        const xx = Math.min(lw - 1, 2 * x + ox), yy = Math.min(lh - 1, 2 * y + oy);
                        v += lv[(yy * lw + xx) * 4 + c];
                    }
                    nd[(y * nw + x) * 4 + c] = v / 4;
                }
            }
            levels.push({ w: nw, h: nh, d: nd });
            lw = nw; lh = nh; lv = nd;
        }
        this.pyr = levels;
    }

    /** Which half-res level holds the «neighbourhood» brightness: a block of ~1/24 of the short side. */
    localLevel() { return localLevelFor(this.w, this.h); }

    _spatialAndTone(p) {
        const { w, h, acc, hist, out, pyr } = this;
        const hD = p.denoise || 0;
        const noise = p.noise || { a: 0, b: 0 };
        const gain = p.gain || 1;
        const T = p.target || PARAMS.target, W = p.white || PARAMS.white;
        const sLoc = p.local || 0;
        const detail = p.detail || 0;
        const lut = p.lut || null;
        const tint = p.motionTint || 0;
        const L0 = this.localLevel();
        const ss = PARAMS.spatialSigma, inv2ss = 1 / (2 * ss * ss);
        const wsp = [];
        for (let oy = -2; oy <= 2; oy++) for (let ox = -2; ox <= 2; ox++) wsp.push(Math.exp(-(ox * ox + oy * oy) * inv2ss));
        const lvC = pyr[0], lvA = pyr[Math.min(L0, pyr.length - 1)], lvB = pyr[Math.min(L0 + 1, pyr.length - 1)];
        const px = [0, 0, 0, 0], pa = [0, 0, 0, 0], pb = [0, 0, 0, 0];
        for (let y = 0; y < h; y++) {
            for (let x = 0; x < w; x++) {
                const i = y * w + x, j = i * 3;
                const r0 = acc[j], g0 = acc[j + 1], b0 = acc[j + 2];
                const Lc = luma(r0, g0, b0);
                const nh = hist[i] || 1;
                const sig = Math.sqrt(Math.max(noise.a + noise.b * Math.max(Lc, 0), PARAMS.noiseFloor) / nh);
                let R = r0, Gc = g0, B = b0, Lb = Lc;
                if (hD > 0) {
                    const rr = hD * sig, inv2r = 1 / (2 * rr * rr);
                    let sw = 0, sr = 0, sg = 0, sb = 0, k = 0;
                    for (let oy = -2; oy <= 2; oy++) {
                        const yy = y + oy < 0 ? 0 : y + oy >= h ? h - 1 : y + oy;
                        for (let ox = -2; ox <= 2; ox++, k++) {
                            const xx = x + ox < 0 ? 0 : x + ox >= w ? w - 1 : x + ox;
                            const q = (yy * w + xx) * 3;
                            const L = luma(acc[q], acc[q + 1], acc[q + 2]);
                            const dl = L - Lc;
                            const wt = wsp[k] * Math.exp(-dl * dl * inv2r);
                            sw += wt; sr += wt * acc[q]; sg += wt * acc[q + 1]; sb += wt * acc[q + 2];
                        }
                    }
                    R = sr / sw; Gc = sg / sw; B = sb / sw; Lb = luma(R, Gc, B);
                }
                const u = (x + 0.5) / w, v = (y + 0.5) / h;
                // detail: lift what the half-res copy has lost, only where the signal stands above the noise
                sampleLevel(lvC, u, v, px);
                if (detail > 0) {
                    const snr = Lb / Math.max(sig, 1e-6);
                    const amt = detail * smoothstep(2, 8, snr);
                    const Ls = Math.max(0, Lb + amt * (Lb - px[3]));
                    const f = Lb > 1e-6 ? Ls / Lb : 1;
                    R *= f; Gc *= f; B *= f; Lb = Ls;
                }
                // colour: from the soft copy where the pixel's own colour is mostly noise
                const rel = sig / Math.max(Lb, 1e-5);
                const cm = smoothstep(0.03, 0.25, rel);
                if (cm > 0 && px[3] > 1e-6) {
                    const cr = px[0] / px[3], cg = px[1] / px[3], cb = px[2] / px[3];
                    const own = Lb > 1e-6 ? 1 / Lb : 0;
                    R = Lb * ((R * own) + (cr - R * own) * cm);
                    Gc = Lb * ((Gc * own) + (cg - Gc * own) * cm);
                    B = Lb * ((B * own) + (cb - B * own) * cm);
                }
                const sat = 1 - (p.desat == null ? 0.7 : p.desat) * smoothstep(0.15, 0.6, rel);
                // exposure
                sampleLevel(lvA, u, v, pa); sampleLevel(lvB, u, v, pb);
                const Lloc = 0.5 * (pa[3] + pb[3]);
                const gl = localGain(Lloc * gain, T, sLoc, PARAMS.localMin, PARAMS.localMax);
                const X = Lb * gain * gl;
                const Y = tone(X, W);
                const f2 = Lb > 1e-7 ? Y / Lb : 0;
                let r = R * f2, g = Gc * f2, b = B * f2;
                // saturation around the output brightness, then keep inside the gamut
                r = Y + (r - Y) * sat; g = Y + (g - Y) * sat; b = Y + (b - Y) * sat;
                const mx = Math.max(r, g, b);
                if (mx > 1) { const q = (mx - Y) > 1e-6 ? (1 - Y) / (mx - Y) : 0; r = Y + (r - Y) * q; g = Y + (g - Y) * q; b = Y + (b - Y) * q; }
                let sr = linearToSrgb(r), sg = linearToSrgb(g), sb = linearToSrgb(b);
                if (lut) {
                    const ys = linearToSrgb(Y);
                    const li = Math.min(255, Math.max(0, Math.round(ys * 255))) * 4;
                    sr = lut[li] / 255; sg = lut[li + 1] / 255; sb = lut[li + 2] / 255;
                }
                if (tint > 0) {
                    const mt = this.motion[i] * tint;
                    sr += (1 - sr) * mt; sg += (0.16 - sg) * mt; sb += (0.1 - sb) * mt;
                }
                const o = i * 4;
                out[o] = Math.round(sr * 255); out[o + 1] = Math.round(sg * 255); out[o + 2] = Math.round(sb * 255); out[o + 3] = 255;
            }
        }
    }

    /** Linear luma of the denoised-by-time picture on a coarse grid (what the GPU reads back for exposure). */
    exposureSamples() {
        const lv = this.pyr[Math.min(3, this.pyr.length - 1)];
        const s = new Float32Array(lv.w * lv.h);
        for (let i = 0; i < s.length; i++) s[i] = lv.d[i * 4 + 3];
        return s;
    }

    /**
     * The grid gl.js reads back (FS_STATS), from the CPU lane: raw input bytes
     * at single pixels, √(lod-3 luma) and motion as bytes.
     */
    stats(rgba, gw, gh, frame) {
        const { w, h } = this;
        const raw = new Uint8Array(gw * gh * 4), avg = new Uint8Array(gw * gh * 4);
        const lv = this.pyr[Math.min(3, this.pyr.length - 1)], px = [0, 0, 0, 0];
        for (let gy = 0; gy < gh; gy++) for (let gx = 0; gx < gw; gx++) {
            const x = Math.min(w - 1, Math.floor((gx + 0.5) / gw * w)), y = Math.min(h - 1, Math.floor((gy + 0.5) / gh * h));
            const o = (gy * gw + gx) * 4, q = (y * w + x) * 4;
            raw[o] = rgba[q]; raw[o + 1] = rgba[q + 1]; raw[o + 2] = rgba[q + 2]; raw[o + 3] = 255;
            sampleLevel(lv, (x + 0.5) / w, (y + 0.5) / h, px);
            avg[o] = Math.round(255 * Math.sqrt(Math.min(1, Math.max(0, px[3]))));
            avg[o + 1] = Math.round(255 * this.motion[y * w + x]);
            avg[o + 2] = Math.round(255 * Math.min(1, this.hist[y * w + x] / 128));
            // a = the shift the history followed (0 when the search was not sure, as in gl.js); 255 = no search ran
            avg[o + 3] = this.lastShift ? (this.lastShift.q >= 0.02 ? Math.round(255 * Math.min(0.98, Math.hypot(this.lastShift.dx, this.lastShift.dy) / 16)) : 0) : 255;
        }
        return { raw, avg, w: gw, h: gh, frame: frame || 0 };
    }

    /** Raw linear luma at sparse pixels — the noise meter's input. */
    rawSamples(stepX, stepY) {
        const { w, h, cur } = this;
        const out = [];
        for (let y = (stepY >> 1); y < h; y += stepY) for (let x = (stepX >> 1); x < w; x += stepX) {
            const j = (y * w + x) * 3;
            out.push(luma(cur[j], cur[j + 1], cur[j + 2]));
        }
        return Float32Array.from(out);
    }
}

/** Same rule on both executors: the half-res level whose texel is ~1/24 of the short side. */
export function localLevelFor(w, h) {
    const half = Math.max(1, Math.min(w, h) >> 1);
    return Math.max(1, Math.round(Math.log2(Math.max(2, half / 24))));
}

/** GL-style bilinear (texel centres, clamp to edge) of one pyramid level → out[4]. */
export function sampleLevel(lv, u, v, out) {
    const fx = u * lv.w - 0.5, fy = v * lv.h - 0.5;
    let x0 = Math.floor(fx), y0 = Math.floor(fy);
    const tx = fx - x0, ty = fy - y0;
    let x1 = x0 + 1, y1 = y0 + 1;
    x0 = x0 < 0 ? 0 : x0 >= lv.w ? lv.w - 1 : x0; x1 = x1 < 0 ? 0 : x1 >= lv.w ? lv.w - 1 : x1;
    y0 = y0 < 0 ? 0 : y0 >= lv.h ? lv.h - 1 : y0; y1 = y1 < 0 ? 0 : y1 >= lv.h ? lv.h - 1 : y1;
    const d = lv.d, W = lv.w;
    for (let c = 0; c < 4; c++) {
        const a = d[(y0 * W + x0) * 4 + c], b = d[(y0 * W + x1) * 4 + c];
        const e = d[(y1 * W + x0) * 4 + c], f = d[(y1 * W + x1) * 4 + c];
        out[c] = (a + (b - a) * tx) + ((e + (f - e) * tx) - (a + (b - a) * tx)) * ty;
    }
    return out;
}

/** Catmull-Rom weights of the four taps around t ∈ [0,1). */
function crWeights(t, o) {
    const t2 = t * t, t3 = t2 * t;
    o[0] = -0.5 * t3 + t2 - 0.5 * t;
    o[1] = 1.5 * t3 - 2.5 * t2 + 1;
    o[2] = -1.5 * t3 + 2 * t2 + 0.5 * t;
    o[3] = 0.5 * t3 - 0.5 * t2;
    return o;
}
/** A 3×3 tent (¼ ½ ¼) read bilinearly at offset t: its weights on the four texels around. */
function tentBilinear(t, o) {
    o[0] = 0.25 * (1 - t);
    o[1] = 0.25 * t + 0.5 * (1 - t);
    o[2] = 0.5 * t + 0.25 * (1 - t);
    o[3] = 0.25 * t;
    return o;
}

/* ───────────────────────── camera shake ───────────────────────── */

/**
 * Which half-size levels the shake search uses: the coarse one keeps at least
 * ~24 texels on the short side (a 16×10 level left almost nothing to compare
 * once the border was cut, and the search answered 28 px for a 4 px shake),
 * then the search walks down one level at a time. 720p → level 3 (±48 px reach).
 */
export function alignLevels(hw, hh) {
    return { a: Math.max(1, Math.min(3, Math.floor(Math.log2(Math.max(1, Math.min(hw, hh)) / 24)))) };
}

/**
 * At the coarse level a candidate's cost is multiplied by 1 + ALIGN_PRIOR·r²
 * (r in coarse texels from no motion): a row of lit windows or a fence repeats
 * every few texels, and without a preference for the smaller motion the search
 * jumped a whole period on one frame in five (a building at night, 480 px).
 */
export const ALIGN_PRIOR = 0.03;
/** Coarse candidates carried to the full-detail decision (see shiftSearch). */
export const ALIGN_HYPOTHESES = 2;

/**
 * The shake search exactly as gl.js runs it (cost + best passes): luma
 * pyramids of the new frame and of the previous history, both at half size;
 * exhaustive ±3 texels at level `lodA` (1/16 of full size at 3), then down
 * the pyramid one level at a time, ±1 around the doubled winner, a parabola
 * through each minimum; the last step (opts.fine) is the half-size level
 * itself with the robust cost. One level at a time: a jump of ×4 with a ±1
 * window lost the shift whenever the coarse answer was 0.4 texel off — both
 * executors then missed by 1–2 px, each its own way (parity stand, 23.10.2026).
 * Returns the scene's motion in FULL-size pixels and the match quality.
 * @param {Array<{w,h,d:Float32Array}>} cur  levels of the new frame's half-size luma
 * @param {Array<{w,h,d:Float32Array}>} prev levels of the previous history's half-size luma
 */
export function shiftSearch(cur, prev, lodA, opts) {
    const A = Math.max(1, Math.min(lodA == null ? 3 : lodA, cur.length - 1));
    const mc = Math.max(levelMean(cur[0]), 1e-6), mp = Math.max(levelMean(prev[0]), 1e-6);
    const prior = opts && opts.prior != null ? opts.prior : ALIGN_PRIOR;
    const step = (L, R, c0x, c0y, pr, want2) => {
        const S = 2 * R + 1, costs = new Float64Array(S * S), shifts = [];
        const cl = cur[L], pl = prev[L], w = cl.w, h = cl.h, m = R + 1;
        const bx = Math.floor(c0x + 0.5), by = Math.floor(c0y + 0.5);
        for (let k = 0; k < S * S; k++) {
            const sx = bx + (k % S) - R, sy = by + ((k / S) | 0) - R;
            let e = 0, n = 0;
            for (let y = m; y < h - m; y++) {
                const yy = Math.max(0, Math.min(pl.h - 1, y - sy));
                for (let x = m; x < w - m; x++) {
                    const xx = Math.max(0, Math.min(pl.w - 1, x - sx));
                    e += Math.abs(cl.d[y * w + x] / mc - pl.d[yy * pl.w + xx] / mp); n++;
                }
            }
            costs[k] = (n ? e / n : 0) * (pr ? 1 + pr * (sx * sx + sy * sy) : 1);
            shifts.push([sx, sy]);
        }
        let bi = 0, avg = 0;
        for (let k = 0; k < costs.length; k++) { avg += costs[k]; if (costs[k] < costs[bi]) bi = k; }
        avg /= costs.length;
        const par = (a, b, c) => { const d = a - 2 * b + c; return d > 1e-9 ? Math.max(-0.5, Math.min(0.5, 0.5 * (a - c) / d)) : 0; };
        const at = (i) => {
            const ix = i % S, iy = (i / S) | 0;
            const fx = ix > 0 && ix < S - 1 ? par(costs[iy * S + ix - 1], costs[i], costs[iy * S + ix + 1]) : 0;
            const fy = iy > 0 && iy < S - 1 ? par(costs[(iy - 1) * S + ix], costs[i], costs[(iy + 1) * S + ix]) : 0;
            return { x: shifts[i][0] + fx, y: shifts[i][1] + fy, q: avg > 1e-9 ? Math.max(0, Math.min(1, (avg - costs[i]) / avg)) : 0 };
        };
        const best = at(bi);
        if (want2) {
            // the runner-up from another basin: a repeating pattern (lit windows, a fence)
            // matches one period away almost as well as the true shift does
            const bx0 = bi % S, by0 = (bi / S) | 0;
            let si = -1;
            for (let k = 0; k < costs.length; k++) {
                if (Math.max(Math.abs(k % S - bx0), Math.abs(((k / S) | 0) - by0)) < 2) continue;
                if (si < 0 || costs[k] < costs[si]) si = k;
            }
            best.second = si >= 0 ? at(si) : null;
        }
        return best;
    };
    const hyp = opts && opts.hyp != null ? opts.hyp : ALIGN_HYPOTHESES;
    const coarse = step(A, 3, 0, 0, prior, hyp > 1 && !!(opts && opts.fine));
    const descend = (b0) => { let b = b0; for (let L = A - 1; L >= 1; L--) b = step(L, 1, b.x * 2, b.y * 2); return b; };
    if (!opts || !opts.fine) { const b = descend(coarse); return { dx: b.x * 4, dy: b.y * 4, q: b.q }; }
    // fine: ±1 at the half-size level itself, with a robust cost (truncated
    // squares) so a thing moving through the frame does not pull the camera's
    // shift towards its own motion; the same 9 candidates gl.js reduces by
    // mipmaps. Twice: the second window is centred on the first one's winner,
    // so a true shift between two candidates (1.5 texels) is never stuck on the
    // edge of the window, where no parabola can be drawn (it read 2.00 for 3).
    const tau2 = (opts.tau || 1) * (opts.tau || 1);
    const cl = cur[0], pl = prev[0], w = cl.w, h = cl.h;
    const fine = (cx0, cy0) => {
        const costs = new Float64Array(9);
        for (let kk = 0; kk < 9; kk++) {
            const sx = cx0 + (kk % 3) - 1, sy = cy0 + ((kk / 3) | 0) - 1;
            let e = 0, n = 0;
            for (let y = 2; y < h - 2; y++) {
                const yy = Math.max(0, Math.min(pl.h - 1, y - sy));
                for (let x = 2; x < w - 2; x++) {
                    const xx = Math.max(0, Math.min(pl.w - 1, x - sx));
                    const d = cl.d[y * w + x] / mc - pl.d[yy * pl.w + xx] / mp;
                    e += Math.min(d * d, tau2); n++;
                }
            }
            costs[kk] = e / Math.max(1, n);
        }
        let bi = 0, avg = 0;
        for (let kk = 0; kk < 9; kk++) { avg += costs[kk]; if (costs[kk] < costs[bi]) bi = kk; }
        avg /= 9;
        const ix = bi % 3, iy = (bi / 3) | 0;
        const par = (p0, p1, p2) => { const d = p0 - 2 * p1 + p2; return d > 1e-12 ? Math.max(-0.5, Math.min(0.5, 0.5 * (p0 - p2) / d)) : 0; };
        const fx = ix === 1 ? par(costs[iy * 3], costs[bi], costs[iy * 3 + 2]) : 0;
        const fy = iy === 1 ? par(costs[ix], costs[bi], costs[6 + ix]) : 0;
        return { x: cx0 + ix - 1, y: cy0 + iy - 1, fx, fy, q: avg > 1e-12 ? Math.max(0, Math.min(1, (avg - costs[bi]) / avg)) : 0, cost: costs[bi] };
    };
    const track = (b) => {
        const r1 = fine(Math.floor(b.x * 2 + 0.5), Math.floor(b.y * 2 + 0.5));
        return fine(Math.floor(r1.x + r1.fx + 0.5), Math.floor(r1.y + r1.fy + 0.5));
    };
    let r2 = track(descend(coarse));
    // the runner-up decided at full detail, where the texture that does not repeat (grass,
    // plaster, noise of the scene itself) tells the true shift from a period away
    if (coarse.second) { const alt = track(descend(coarse.second)); if (alt.cost < r2.cost) r2 = alt; }
    let dx = (r2.x + r2.fx) * 2, dy = (r2.y + r2.fy) * 2;
    // a still camera: the tiny shifts a search always finds on a static scene are noise
    if (Math.hypot(dx, dy) < (opts.deadband == null ? 0.25 : opts.deadband)) { dx = 0; dy = 0; }
    return { dx, dy, q: r2.q };
}
function levelMean(lv) { let s = 0; for (let i = 0; i < lv.d.length; i++) s += lv.d[i]; return lv.d.length ? s / lv.d.length : 0; }

/** Box pyramid of a single-channel image (level 0 = the image). */
export function lumaPyramid(d, w, h) {
    const levels = [{ w, h, d }];
    let lw = w, lh = h, lv = d;
    while (lw > 1 || lh > 1) {
        const nw = Math.max(1, lw >> 1), nh = Math.max(1, lh >> 1), nd = new Float32Array(nw * nh);
        for (let y = 0; y < nh; y++) for (let x = 0; x < nw; x++) {
            let v = 0;
            for (let oy = 0; oy < 2; oy++) for (let ox = 0; ox < 2; ox++) v += lv[Math.min(lh - 1, 2 * y + oy) * lw + Math.min(lw - 1, 2 * x + ox)];
            nd[y * nw + x] = v / 4;
        }
        levels.push({ w: nw, h: nh, d: nd });
        lw = nw; lh = nh; lv = nd;
    }
    return levels;
}

/**
 * Global shift between two coarse luma images (same size), by exhaustive
 * search of ±r texels and a parabola through the best and its neighbours.
 * Mean-normalised (exposure may change between frames). Returns [dx, dy] in
 * coarse texels and the match quality (0 — no structure to lock onto).
 */
export function estimateShift(prev, cur, w, h, r) {
    const R = r || 4;
    let mp = 0, mc = 0;
    for (let i = 0; i < prev.length; i++) { mp += prev[i]; mc += cur[i]; }
    mp /= prev.length; mc /= cur.length;
    if (!(mp > 1e-6) || !(mc > 1e-6)) return { dx: 0, dy: 0, q: 0 };
    const kp = 1 / mp, kc = 1 / mc;
    const S = 2 * R + 1, cost = new Float64Array(S * S);
    const x0 = R, y0 = R, x1 = w - R, y1 = h - R;
    for (let sy = -R; sy <= R; sy++) {
        for (let sx = -R; sx <= R; sx++) {
            let e = 0;
            for (let y = y0; y < y1; y++) {
                const rc = y * w, rp = (y - sy) * w;
                for (let x = x0; x < x1; x++) e += Math.abs(cur[rc + x] * kc - prev[rp + x - sx] * kp);
            }
            cost[(sy + R) * S + (sx + R)] = e;
        }
    }
    let bi = 0;
    for (let i = 1; i < cost.length; i++) if (cost[i] < cost[bi]) bi = i;
    const bx = bi % S, by = (bi / S) | 0;
    const par = (a, b, c) => { const d = a - 2 * b + c; return d > 1e-12 ? 0.5 * (a - c) / d : 0; };
    const fx = bx > 0 && bx < S - 1 ? par(cost[by * S + bx - 1], cost[bi], cost[by * S + bx + 1]) : 0;
    const fy = by > 0 && by < S - 1 ? par(cost[(by - 1) * S + bx], cost[bi], cost[(by + 1) * S + bx]) : 0;
    // quality: how much better the best shift is than standing still
    const c0 = cost[R * S + R], cb = cost[bi];
    let avg = 0; for (let i = 0; i < cost.length; i++) avg += cost[i]; avg /= cost.length;
    const q = avg > 1e-12 ? Math.max(0, Math.min(1, (avg - cb) / avg)) : 0;
    return { dx: bx - R + fx, dy: by - R + fy, q, still: c0 <= cb * 1.0001 };
}
